import React, { useState, useEffect } from 'react';
import {
    NSLabel, NSInputNumberStepper,
} from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import {
    FORM_ROW_CLASS_NAME,
    FORM_LABEL_CLASS_NAME,
    proFormaModelParkingOptions,
    STRUCTURED_PARKING,
    SURFACE_PARKING,
    parkingOptions,
    UNDERGROUND_PARKING,
    TRAILER_PARKING,
    industrialParkingOptions,
    NO_PARKING,
    noParkingOption,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { IProFormaValues } from 'views/ProFormaTable/types';
import { ProFormaModelIds } from 'constants/proForma';
import ParkingSelector from 'views/ProFormaTable/components/ProForma/ProFormaGeneralInformation/ParkingSelector/ParkingSelector';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';

export interface IProFormaSizeParkingProps {
    values: IProFormaValues;
    handleInputChange: (name: string, value: string | number) => void;
}

const ProFormaSizeParking = ({
    values, handleInputChange,
}: IProFormaSizeParkingProps) => {
    const { handleUpdateProForma, proForma } = useProFormaSocketContext();

    const [selectedParkingOptions, setSelectedParkingOptions] = useState<IOptionAnyValue[]>([]);
    const updateProFormaParkingTypes = (parkingTypes: IOptionAnyValue[], event: any) => {
        const isStructuredParkingSelected = parkingTypes.find(type => type.value === STRUCTURED_PARKING);
        const isSurfaceParkingSelected = parkingTypes.find(type => type.value === SURFACE_PARKING);
        const isUndergroundParkingSelected = parkingTypes.find(type => type.value === UNDERGROUND_PARKING);
        const isTrailerParkingSelected = parkingTypes.find(type => type.value === TRAILER_PARKING);
        const isNoParkingSelected = parkingTypes.find(type => type.value === NO_PARKING);
        const dataToUpdate = [];

        // Removing parking types
        if (!isStructuredParkingSelected || isNoParkingSelected) dataToUpdate.push({ key: 'structuredParkingSpaces', value: null });
        if (!isSurfaceParkingSelected || isNoParkingSelected) dataToUpdate.push({ key: 'surfaceParkingSpaces', value: null });
        if (!isUndergroundParkingSelected || isNoParkingSelected) dataToUpdate.push({ key: 'undergroundParkingSpaces', value: null });
        if (!isTrailerParkingSelected || isNoParkingSelected) dataToUpdate.push({ key: 'trailerParkingSpaces', value: null });
        // Adding selected parking types
        if (isStructuredParkingSelected && !values.structuredParkingSpaces) dataToUpdate.push({ key: 'structuredParkingSpaces', value: 0 });
        if (isSurfaceParkingSelected && !values.surfaceParkingSpaces) dataToUpdate.push({ key: 'surfaceParkingSpaces', value: 0 });
        if (isUndergroundParkingSelected && !values.undergroundParkingSpaces) dataToUpdate.push({ key: 'undergroundParkingSpaces', value: 0 });
        if (isTrailerParkingSelected && !values.trailerParkingSpaces) dataToUpdate.push({ key: 'trailerParkingSpaces', value: 0 });

        handleUpdateProForma(dataToUpdate);
    };

    useEffect(() => {
        const initialSelectedParkingOptions = [];

        if (values.structuredParkingSpaces != null) {
            initialSelectedParkingOptions.push(parkingOptions[0]);
        }
        if (values.surfaceParkingSpaces != null) {
            initialSelectedParkingOptions.push(parkingOptions[1]);
        }
        if (values.undergroundParkingSpaces != null) {
            initialSelectedParkingOptions.push(parkingOptions[2]);
        }
        if (values.trailerParkingSpaces != null) {
            initialSelectedParkingOptions.push(industrialParkingOptions[industrialParkingOptions.length - 1]);
        }
        if (initialSelectedParkingOptions.length === 0) {
            initialSelectedParkingOptions.push(noParkingOption);
        }
        setSelectedParkingOptions(initialSelectedParkingOptions);
    }, [values.structuredParkingSpaces, values.surfaceParkingSpaces, values.undergroundParkingSpaces, values.trailerParkingSpaces]);

    return (
        <>
            <div className={FORM_ROW_CLASS_NAME}>
                <NSLabel className={FORM_LABEL_CLASS_NAME}>Parking</NSLabel>
                <ParkingSelector
                    options={proFormaModelParkingOptions[proForma.proFormaModelId as ProFormaModelIds] || 
                             proFormaModelParkingOptions[ProFormaModelIds.APARTMENT]}
                    value={selectedParkingOptions}
                    setSelectedParkingOptions={(options: IOptionAnyValue[]) => setSelectedParkingOptions(options)}
                    onBlur={(event: React.FocusEvent<HTMLElement>) => updateProFormaParkingTypes(selectedParkingOptions, event)}
                />
            </div>

            <RenderIf isTrue={selectedParkingOptions.find(parkingOption => parkingOption.value === STRUCTURED_PARKING)}>
                <div className={FORM_ROW_CLASS_NAME}>
                    <NSLabel className={FORM_LABEL_CLASS_NAME}>Structured parking spaces</NSLabel>
                    <NSInputNumberStepper
                        name="structuredParkingSpaces"
                        value={values.structuredParkingSpaces && values.structuredParkingSpaces}
                        onChange={(count: number) => {
                            handleUpdateProForma([{ key: 'structuredParkingSpaces', value: count }]);
                            handleInputChange('structuredParkingSpaces', count);
                        }}
                        min={0.1}
                        inputGroupClassName="w-25"
                        step={1}
                    />
                </div>
            </RenderIf>

            <RenderIf isTrue={selectedParkingOptions.find(parkingOption => parkingOption.value === SURFACE_PARKING)}>
                <div className={FORM_ROW_CLASS_NAME}>
                    <NSLabel className={FORM_LABEL_CLASS_NAME}>Surface parking spaces</NSLabel>
                    <NSInputNumberStepper
                        name="surfaceParkingSpaces"
                        value={values.surfaceParkingSpaces}
                        onChange={(count: number) => {
                            handleUpdateProForma([{ key: 'surfaceParkingSpaces', value: count }]);
                            handleInputChange('surfaceParkingSpaces', count);
                        }}
                        min={1}
                        inputGroupClassName="w-25"
                        step={1}
                    />
                </div>
            </RenderIf>

            <RenderIf isTrue={selectedParkingOptions.find(parkingOption => parkingOption.value === UNDERGROUND_PARKING)}>
                <div className={FORM_ROW_CLASS_NAME}>
                    <NSLabel className={FORM_LABEL_CLASS_NAME}>Underground parking spaces</NSLabel>
                    <NSInputNumberStepper
                        name="undergroundParkingSpaces"
                        value={values.undergroundParkingSpaces}
                        onChange={(count: number) => handleUpdateProForma([{ key: 'undergroundParkingSpaces', value: count }])}
                        min={0.1}
                        inputGroupClassName="w-25"
                        step={1}
                    />
                </div>
            </RenderIf>

            <RenderIf isTrue={selectedParkingOptions.find(parkingOption => parkingOption.value === TRAILER_PARKING)}>
                <div className={FORM_ROW_CLASS_NAME}>
                    <NSLabel className={FORM_LABEL_CLASS_NAME}>Trailer parking spaces</NSLabel>
                    <NSInputNumberStepper
                        name="trailerParkingSpaces"
                        value={values.trailerParkingSpaces}
                        onChange={(count: number) => handleUpdateProForma([{ key: 'trailerParkingSpaces', value: count }])}
                        min={0.1}
                        inputGroupClassName="w-25"
                        step={1}
                    />
                </div>
            </RenderIf>
        </>
    );
};

export default ProFormaSizeParking;
